import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { fetchEpg } from "./channelScheduleApi";
import "./BroadcastStudio.css";
import "./EpgPage.css";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return "";
  }
}

/**
 * Electronic Program Guide — reusable grid for web (and future TV/mobile clients via same API).
 */
function EpgPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const from = new Date();
        from.setMinutes(0, 0, 0);
        const to = new Date(from.getTime() + 6 * 3600000);
        const grid = await fetchEpg({
          from: from.toISOString(),
          to: to.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        });
        if (!cancelled) {
          setData(grid);
          setError(grid ? "" : "No EPG data.");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "EPG failed.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bcast-studio epg-page">
      <Helmet>
        <title>Program Guide · TPG</title>
      </Helmet>
      <header className="bcast-studio__header">
        <div>
          <p className="bcast-studio__eyebrow">Electronic Program Guide</p>
          <h1>What&apos;s On</h1>
        </div>
        <nav className="bcast-studio__nav">
          <Link to="/studio/timeline">Timeline Studio</Link>
          <Link to="/channels">All Channels</Link>
        </nav>
      </header>

      {loading && <p className="bcast-studio__status">Loading guide…</p>}
      {error && <p className="bcast-studio__error">{error}</p>}

      {data && (
        <div className="epg-grid">
          {(data.channels || []).map((row) => {
            const ch = row.channel || {};
            const now = row.now || {};
            const current = now.current;
            const next = now.next;
            return (
              <article key={ch.id || ch.slug} className="epg-row">
                <div className="epg-channel">
                  {ch.logo ? (
                    <img src={ch.logo} alt="" className="epg-channel__logo" />
                  ) : (
                    <div className="epg-channel__logo epg-channel__logo--placeholder" />
                  )}
                  <div>
                    <h2>
                      {ch.channel_number != null ? (
                        <span className="epg-channel__num">{ch.channel_number}</span>
                      ) : null}
                      {ch.name}
                    </h2>
                    <p className="epg-channel__meta">
                      {now.phase === "live" || (current && current.live_state === "live") ? (
                        <span className="epg-live">LIVE</span>
                      ) : (
                        <span>{now.phase || "—"}</span>
                      )}
                    </p>
                    <Link className="bcast-btn" to={"/channel/" + (ch.slug || ch.unique_id || ch.id)}>
                      Watch Live
                    </Link>
                  </div>
                </div>
                <div className="epg-now">
                  <p className="epg-label">Now</p>
                  <h3>{(current && current.title) || "Off air"}</h3>
                  <p>
                    {current
                      ? formatTime(current.starts_at) + " – " + formatTime(current.ends_at)
                      : "—"}
                  </p>
                </div>
                <div className="epg-next">
                  <p className="epg-label">Up Next</p>
                  <h3>{(next && next.title) || "—"}</h3>
                  <p>{next ? formatTime(next.starts_at) : ""}</p>
                </div>
                <div className="epg-upcoming">
                  <p className="epg-label">Upcoming</p>
                  <ul>
                    {(row.programs || []).slice(0, 6).map((p) => (
                      <li key={p.id}>
                        <span>{formatTime(p.starts_at)}</span>
                        <strong>
                          {p.title || p.entry_kind}
                          {p.is_live ? " · LIVE" : ""}
                        </strong>
                        {p.duration_seconds != null && (
                          <em>{Math.round(p.duration_seconds / 60)}m</em>
                        )}
                      </li>
                    ))}
                  </ul>
                  <Link to={"/channel/" + (ch.slug || ch.unique_id) + "/schedule"}>Full schedule</Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default EpgPage;
